import ImageBannerContainer from '@/containers/V3/Banner/ImageBannerContainer';
import useWebview from '@/hooks/V3/useWebview';
import IconBannerContainer from '@/containers/V3/Banner/IconBannerContainer';
import { Button } from '@/components/V3/Button';
import BlogItem from './_components/BlogItem';
import { CPLAT_API_ROUTES, usePost } from '@/api';
import { useCallback, useEffect, useState } from 'react';
import { ApiStatusType, BlogPosting } from '@/types/discover';
import BlogErrorContainer from './_components/BlogErrorContainer';
import BlogItemSkeleton from './_components/BlogItemSkeleton';
import { useRecoilValue } from 'recoil';
import { deviceUuidState, safeAreaInsetsState } from '@/store/atom';
import { useAppSelector } from '@/redux/store';
import { useLog } from '@/hooks/V3/useLog';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';
import MembershipPreviewFloatingCTA from '@/components/membership/MembershipPreviewFloatingCTA';

const DiscoverMain = () => {
  const user = useAppSelector((state) => state.user);
  const { postMessage, requestMembershipRequired } = useWebview();
  const isPreviewMode = useMembershipPreviewMode();
  const [blogList, setBlogList] = useState<Array<BlogPosting>>([]);
  const [apiStatus, setApiStatus] = useState<ApiStatusType>('loading');
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);
  const [blogLastNumber, setBlogLastNumber] = useState<number>(5);
  const { setUserEventHistory } = useLog(user.UserNo);
  const deviceUuid = useRecoilValue(deviceUuidState);

  const { mutate: getBlogPosting } = usePost(CPLAT_API_ROUTES.getBlogPosting);

  const handleGetBlogPosting = useCallback(() => {
    setApiStatus('loading');
    getBlogPosting(
      {},
      {
        onSuccess(res) {
          const { code, data } = res.data;
          if (code === '200') {
            const BlogPostingList = data[0].BlogPostingList;
            setBlogList(BlogPostingList);
            setApiStatus('success');
          } else {
            setApiStatus('error');
          }
        },
        onError() {
          setApiStatus('error');
        },
      },
    );
  }, [getBlogPosting]);

  const handlePreviewAiClick = () => {
    if (!isPreviewMode) return false;

    requestMembershipRequired('productOptimization');
    return true;
  };

  useEffect(() => {
    handleGetBlogPosting();
  }, [handleGetBlogPosting]);

  return (
    <main
      className="relative flex h-screen select-none flex-col overflow-hidden bg-white"
      style={{
        paddingTop: safeAreaInsets.topInset,
        paddingBottom: safeAreaInsets.bottomInset,
      }}
    >
      <div className="flex-1 overflow-y-auto px-[20px] scrollbar-hide">
        <div className="mb-[20px] mt-[28px] text-gr-900 Title1S28">발견</div>
        {/* 키워드 검색창은 정책 확인 전까지 숨김 */}
        <div data-membership-preview-interactive={isPreviewMode ? 'true' : undefined}>
          <ImageBannerContainer
            variant={'highAndLowGame'}
            title={'하이앤로우 게임'}
            description={'어떤 키워드의 검색량이 많을까요?'}
            onClick={() => {
              postMessage({
                action: 'navigateApp',
                data: {
                  key: 'HighAndLowStart',
                  name: 'HighAndLowStart',
                },
              });
            }}
            className="mb-[24px] mt-[20px]"
          />
        </div>
        <div className="mb-[20px] flex items-center gap-[4px]">
          <span className="text-gr-800 Title3S18">Sellerbox</span>
          <span
            className="Title3S18"
            style={{
              background: 'linear-gradient(270deg, #78B0FF 0%, #E483FF 47%, #FF8585 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            AI
          </span>
        </div>
        <div className="flex flex-col gap-[10px]">
          {/*TODO : AI 챗봇 안정화시 해당 주석 해제*/}
          {/* <IconBannerContainer
            title={'셀러박스 AI 챗봇'}
            description={'원하는 판매 데이터를 바로 알아보세요'}
            onClick={() => {
              postMessage({
                action: 'navigateApp',
                data: {
                  key: 'AIChatBot',
                  name: 'AIChatBot',
                },
              });
            }}
            badge={
              <Badge variant={'blue'} height={'22'} width={'4'}>
                Beta
              </Badge>
            }
          /> */}
          <div data-membership-preview-interactive={isPreviewMode ? 'true' : undefined}>
            <IconBannerContainer
              title={'상품명 제작'}
              description={'상위 노출시킬 수 있는 상품명을 만들어요'}
              onClick={() => {
                if (handlePreviewAiClick()) return;

                postMessage({
                  action: 'navigateApp',
                  data: {
                    key: 'MakeNameWebview',
                    name: 'MakeNameWebview',
                  },
                });
              }}
            />
          </div>
          {/* <IconBannerContainer
            title={'상품 페이지 진단'}
            description={'상세 페이지의 개선점을 파악하고 최적화 해요'}
            onClick={() => {
              postMessage({
                action: 'navigateApp',
                data: {
                  key: 'DiagnoseProductWebview',
                  name: 'DiagnoseProductWebview',
                },
              });
            }}
          /> */}
          <div data-membership-preview-interactive={isPreviewMode ? 'true' : undefined}>
            <IconBannerContainer
              title={'도매몰 상품명 바꾸기'}
              description={'기존 상품명을 변경해 상품을 상위 노출 시켜요'}
              onClick={() => {
                if (handlePreviewAiClick()) return;

                postMessage({
                  action: 'navigateApp',
                  data: {
                    key: 'ChangeNameWebview',
                    name: 'ChangeNameWebview',
                  },
                });
              }}
            />
          </div>
        </div>
        <div className="mb-[16px] mt-[40px]">
          <span className="text-gr-800 Title3S18">블로그</span>
        </div>
        {apiStatus === 'loading' ? (
          <div className="mb-[20px] flex w-full flex-col gap-[12px]">
            <BlogItemSkeleton />
            <BlogItemSkeleton />
          </div>
        ) : apiStatus === 'error' ? (
          <div data-membership-preview-interactive={isPreviewMode ? 'true' : undefined}>
            <BlogErrorContainer onClick={handleGetBlogPosting} />
          </div>
        ) : (
          <div className="pb-[80px]">
            <div className="mb-[20px] flex w-full flex-col gap-[12px]">
              {blogList.slice(0, 2).map((item, index) => (
                <div
                  key={`${item.Title} ${index}`}
                  data-membership-preview-interactive={isPreviewMode ? 'true' : undefined}
                >
                  <BlogItem
                    imageUrl={item.ThmubnailUrl}
                    subtitle={item.CategoryPublicName}
                    title={item.Title}
                    onClick={() => {
                      postMessage({
                        action: 'navigateApp',
                        data: {
                          key: 'BlogWebviewPage',
                          name: 'BlogWebviewPage',
                          params: {
                            url: item.Url,
                          },
                        },
                      });
                      setUserEventHistory({
                        eventName: 'click_move_blog_article',
                        eventValue: item.Title,
                        deviceUuid,
                      });
                    }}
                  />
                </div>
              ))}
            </div>
            <div data-membership-preview-interactive={isPreviewMode ? 'true' : undefined}>
              <ImageBannerContainer
                variant={'newsLetter'}
                title={'셀박레터 구독하기'}
                description={'이커머스 소식을 빠르게 접하세요!'}
                onClick={() => {
                  postMessage({
                    action: 'appBottomSheetWebview',
                    data: {
                      url: 'https://sellerboxletter.stibee.com/subscribe/',
                    },
                  });
                  setUserEventHistory({
                    eventName: 'click_news_letter_subscribe',
                    deviceUuid,
                  });
                }}
                className="my-[16px]"
              />
            </div>
            <div className="mb-[20px] flex w-full flex-col gap-[12px]">
              {blogList.slice(2, 4).map((item, index) => (
                <div
                  key={`${item.Title} ${index}`}
                  data-membership-preview-interactive={isPreviewMode ? 'true' : undefined}
                >
                  <BlogItem
                    imageUrl={item.ThmubnailUrl}
                    subtitle={item.CategoryPublicName}
                    title={item.Title}
                    onClick={() => {
                      postMessage({
                        action: 'navigateApp',
                        data: {
                          key: 'BlogWebviewPage',
                          name: 'BlogWebviewPage',
                          params: {
                            url: item.Url,
                          },
                        },
                      });
                      setUserEventHistory({
                        eventName: 'click_move_blog_article',
                        eventValue: item.Title,
                        deviceUuid,
                      });
                    }}
                  />
                </div>
              ))}
            </div>
            {blogLastNumber >= 9 && (
              <div className="mb-[20px] flex w-full flex-col gap-[12px]">
                {blogList.slice(5, blogLastNumber).map((item, index) => (
                  <div
                    key={`${item.Title} ${index}`}
                    data-membership-preview-interactive={isPreviewMode ? 'true' : undefined}
                  >
                    <BlogItem
                      imageUrl={item.ThmubnailUrl}
                      subtitle={item.CategoryPublicName}
                      title={item.Title}
                      onClick={() => {
                        postMessage({
                          action: 'navigateApp',
                          data: {
                            key: 'BlogWebviewPage',
                            name: 'BlogWebviewPage',
                            params: {
                              url: item.Url,
                            },
                          },
                        });
                        setUserEventHistory({
                          eventName: 'click_move_blog_article',
                          eventValue: item.Title,
                          deviceUuid,
                        });
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
            {blogLastNumber <= blogList.length && (
              <div data-membership-preview-interactive={isPreviewMode ? 'true' : undefined}>
                <Button
                  variant={'OutlineLow'}
                  height={'44'}
                  width={'full'}
                  onClick={() => {
                    setBlogLastNumber((prev) => prev + 4);
                    setUserEventHistory({
                      eventName: 'click_more_blog_article',
                      deviceUuid,
                    });
                  }}
                >
                  블로그 글 더보기
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      {isPreviewMode && <MembershipPreviewFloatingCTA />}
    </main>
  );
};

export default DiscoverMain;
